import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { environment } from "src/environments/environment";
import { Observable, catchError, map, of, shareReplay, tap, throwError } from "rxjs";
import {
  AdministerMedicationLookupItem,
  ChecklistQuestion,
  ChecklistQuestionsCache,
  EncounterChecklistQuestion,
  EnumerationItem,
  IcdCode,
  ProcedureRoomInfo,
  Provider,
  Resource,
  QuestionLookup,
} from "../models/lookup.model";
import { ApplicationCacheService } from "./application-cache.service";
import { ApiStatus, ReferralApis } from "./utils";
import { cloneDeep, concat } from "lodash";
import { Template } from "../models/templates.model";
import { Prompt } from "../models/prompts.model";
import { CompanyUser } from "../models/application-context.model";

@Injectable({
  providedIn: "root",
})
export class LookupService {
  newPatientTimeSlot = [
    {
      cptCode: "99341",
      duration: 15,
    },
    {
      cptCode: "99342",
      duration: 30,
    },
    {
      cptCode: "99343",
      duration: 60,
    },
    {
      cptCode: "99344",
      duration: 75,
    },
  ];
  existingPatientTimeSlot = [
    {
      cptCode: "99348",
      duration: 30,
    },
    {
      cptCode: "99349",
      duration: 40,
    },
    {
      cptCode: "99350",
      duration: 60,
    },
  ];
  
  // Cache for global questions that don't change per encounter
  private questionsCache: Map<string, QuestionLookup[]> = new Map();
  private hasPreloaded: boolean = false; // Track if preloading has occurred
  
  // Cache for templates
  private templatesCache: Template[] | null = null;
  
  // Cache for prompts
  private promptsCache: Prompt[] | null = null;
  /** Shared company-users request so repeated app-context initialization does not duplicate the HTTP call. */
  private allUsersCache$: Observable<CompanyUser[]> | null = null;
  /** Shared providers request (app context + route data loader). */
  private providersCache$: Observable<Provider[]> | null = null;
  /** Shared schedule-resources request (app context + route data loader). */
  private resourcesCache$: Observable<Resource[]> | null = null;
  
  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService
  ) {
    // Don't preload in constructor - it runs before authentication
    // Preloading will be triggered after user is authenticated via initializeGlobalData()
  }

  getIcdCodes(searchTerm: string): Observable<IcdCode[]> {
    if (!searchTerm) {
      return of([]);
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/icd-codes?term=${encodeURIComponent(searchTerm)}`;
    return this._dataService.get(url);
  }

  getIcdCodesByCodeName(codeName: string): Observable<IcdCode[]> {
    if (!codeName) {
      return of([]);
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/icd-codes/${encodeURIComponent(codeName)}`;
    return this._dataService.get(url);
  }

  getCptCodes(searchTerm: string): Observable<EnumerationItem[]> {
    if (!searchTerm) {
      return of([]);
    }
    const url =
      this.apiBillerUrl +
      `api/v1/cptcodes/search?term=${encodeURIComponent(searchTerm)}`;
    return this._dataService.get(url).pipe(
      map((cpts) => {
        return cpts.map((cpt) => ({
          displayName: cpt.code + " - " + cpt.description,
          value: cpt.code,
          isAuthRequired: !!cpt.isAuthRequired,
        }));
      })
    );
  }

  getCptCodeDetails(codes: string[]): Observable<
    Array<{ code: string | number; isAuthRequired?: boolean }>
  > {
    const payload = (codes || []).map((code) => String(code).trim()).filter(Boolean);
    if (!payload.length) {
      return of([]);
    }
    const url = this.apiBillerUrl + `api/v1/cptcodes/details`;
    return this._dataService.post(url, payload);
  }

  getTemplateCptCodes(searchTerm: string): Observable<EnumerationItem[]> {
    if (!searchTerm) {
      return of([]);
    }
    const url =
      this.apiBillerUrl +
      `api/v1/cptcodes/search?term=${encodeURIComponent(searchTerm)}`;
    return this._dataService.get(url).pipe(
      map((cpts) => {
        return cpts.map((cpt) => ({
          displayName: cpt.code + " " + cpt.description,
          cptCodeId: cpt.id,
          value: cpt.code,
          isAuthRequired: !!cpt.isAuthRequired,
        }));
      })
    );
  }

  getModifiers(cptCode: string): Observable<string[]> {
    const url = this.apiBillerUrl + `api/v1/cptcodes/modifiers/${cptCode}`;
    return this._dataService.get(url);
  }

  getAutoGeneratedCPTCode(): Observable<{
    newPatient: any[];
    existingPatient: any[];
  }> {
    const payload = concat(
      this.newPatientTimeSlot.map((slot) => slot.cptCode),
      this.existingPatientTimeSlot.map((slot) => slot.cptCode)
    );

    const url = this.apiBillerUrl + `api/v1/cptcodes/details`;
    return this._dataService.post(url, payload).pipe(
      map((cpts) => ({
        newPatient: this.newPatientTimeSlot.map((slot) => ({
          cptCode: slot.cptCode,
          duration: slot.duration,
          description:
            cpts.find((cpt) => cpt.code === slot.cptCode)?.description || "",
        })),
        existingPatient: this.existingPatientTimeSlot.map((slot) => ({
          cptCode: slot.cptCode,
          duration: slot.duration,
          description:
            cpts.find((cpt) => cpt.code === slot.cptCode)?.description || "",
        })),
      })),
      catchError((error) => {
return of(null);
      })
    );
  }

  getMedications(searchTerm: string): Observable<any[]> {
    if (!searchTerm) {
      return of([]);
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/medications/${encodeURIComponent(searchTerm)}`;
    return this._dataService.get(url);
  }

  getAdministerMedicationsList(
    name: string
  ): Observable<AdministerMedicationLookupItem[]> {
    if (!name) {
      return of([]);
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/administer-medications-list/${encodeURIComponent(name)}`;
    return this._dataService.get(url);
  }

  getAdministerMedicationsByIds(
    ids: string[]
  ): Observable<AdministerMedicationLookupItem[]> {
    const unique = [
      ...new Set(
        (ids ?? []).map((id) => String(id ?? "").trim()).filter(Boolean)
      ),
    ];
    if (!unique.length) {
      return of([]);
    }
    let params = new HttpParams();
    for (const id of unique) {
      params = params.append("ids", id);
    }
    const url = `${this.apiBaseUrl}api/v1/lookup/administer-medications-list/by-ids`;
    return this._dataService.get(url, { params });
  }

  getMedicationStrength(id: string) {
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/medications/strength/${encodeURIComponent(id)}`;
    return this._dataService.get(url);
  }

  getMedicationForms(id: string) {
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/medications/forms/${encodeURIComponent(id)}`;
    return this._dataService.get(url);
  }

  getMedicationRoutes(id: string) {
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/medications/routes/${encodeURIComponent(id)}`;
    return this._dataService.get(url);
  }

  getAllergies(searchTerm: string): Observable<EnumerationItem[]> {
    if (!searchTerm) {
      return of([]);
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/lookup/allergies?term=${encodeURIComponent(searchTerm)}`;
    return this._dataService.get(url).pipe(
      map((allergies) => {
        return allergies.map((a) => ({
          // TODO: this is the id an title what will be returned from drugbank api
          //displayName: a.title,
          //value: a.drugbank_id
          // for the time being we are going to use local data so using id and name here.
          displayName: a.name,
          value: a.id,
        }));
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  getImplants(): Observable<EnumerationItem[]> {
    const url = `${this.apiBaseUrl}api/v1/implants`;
    return this._dataService.get(url).pipe(
      map((allergies) => {
        return allergies.map((i) => ({
          displayName: i.name,
          value: i.id,
        }));
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  getVaccineList(): Observable<EnumerationItem[]> {
    const url = `${this.apiBaseUrl}api/v1/immunizations`;
    return this._dataService.get(url).pipe(
      map((immunizations) => {
        return immunizations.map((i) => ({
          displayName: i.name,
          value: i.id,
        }));
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  getPayers(searchTerm: string): Observable<{ name: string }[]> {
    if (!searchTerm) {
      return of([]);
    }
    const url = `${this.apiBaseUrl}api/v1/lookup/payer-list?term=${searchTerm}`;
    return this._dataService.get(url);
  }

  /**
   * Initialize global data including questions, templates, and prompts
   * This is safe to call multiple times - it will only execute once
   * Should be called after user is authenticated
   * @param pqrstaQuestions - PQRSTA questions from ChecklistQuestions
   * @param opqrstQuestions - OPQRST questions from ChecklistQuestions
   * @param historyQuestions - History questions (category='History') from ChecklistQuestions
   */
  public initializeGlobalData(pqrstaQuestions: ChecklistQuestion[] = [], opqrstQuestions: ChecklistQuestion[] = [], historyQuestions: ChecklistQuestion[] = []): void {
    if (this.hasPreloaded) {
      console.log('Global questions already preloaded, skipping');
      return;
    }
    
    this.hasPreloaded = true;
    
    // Populate question caches from ChecklistQuestions
    if (pqrstaQuestions.length > 0) {
      this.questionsCache.set('PQRSTA', pqrstaQuestions as any);
      this._applicationCacheService.setPqrstaQuestionsCache(pqrstaQuestions as any);
      // console.log('✓ PQRSTA questions cached from ChecklistQuestions');
    }
    
    if (opqrstQuestions.length > 0) {
      this.questionsCache.set('OPQRST', opqrstQuestions as any);
      this._applicationCacheService.setOpqrstQuestionsCache(opqrstQuestions as any);
      // console.log('✓ OPQRST questions cached from ChecklistQuestions');
    }
    
    if (historyQuestions.length > 0) {
      // Group history questions by area for easy retrieval
      const medicalHistory = historyQuestions.filter(q => q.area === 'Medical / Surgical History');
      const socialHistory = historyQuestions.filter(q => q.area === 'Social History');
      const familyHistory = historyQuestions.filter(q => q.area === 'Family History');
      
      if (medicalHistory.length > 0) {
        this.questionsCache.set('Medical / Surgical History', medicalHistory as any);
      }
      if (socialHistory.length > 0) {
        this.questionsCache.set('Social History', socialHistory as any);
      }
      if (familyHistory.length > 0) {
        this.questionsCache.set('Family History', familyHistory as any);
      }
      
      // Store all history questions in the cache
      this._applicationCacheService.setHistoryQuestionsCache(historyQuestions as any);
      // console.log('✓ History questions cached from ChecklistQuestions');
    } else {
      console.warn('⚠️ No History questions found in ChecklistQuestions API response');
    }

    // Load templates
    this.loadTemplates().subscribe({
      next: () => {
        // console.log('✓ Templates pre-loaded and cached');
      },
      error: (err) => {
        this.hasPreloaded = false; // Reset flag on error to allow retry
      }
    });

    // Load prompts
    this.loadPrompts().subscribe({
      next: () => {
        // console.log('✓ Prompts pre-loaded and cached');
      },
      error: (err) => {
        this.hasPreloaded = false; // Reset flag on error to allow retry
      }
    });
  }

  /**
   * Get questions by type from cache (no API calls)
   * PQRSTA, OPQRST, Medical / Surgical History, Social History, and Family History are loaded from ChecklistQuestions during initialization
   * @param type Question type (PQRSTA, OPQRST, Medical / Surgical History, Social History, Family History, etc.)
   * @returns Observable of questions
   */
  getQuestionsByType(type: string): Observable<QuestionLookup[]> {
    // Check if questions are already in local cache
    if (this.questionsCache.has(type)) {
      console.log(`Returning ${type} questions from local cache`);
      return of(this.questionsCache.get(type)!);
    }

    // Check if questions are in application cache
    if (type === 'PQRSTA' && this._applicationCacheService.applicationCache.pqrstaQuestions?.length > 0) {
      console.log('Returning PQRSTA questions from application cache');
      this.questionsCache.set(type, this._applicationCacheService.applicationCache.pqrstaQuestions);
      return of(this._applicationCacheService.applicationCache.pqrstaQuestions);
    }

    if (type === 'OPQRST' && this._applicationCacheService.applicationCache.opqrstQuestions?.length > 0) {
      console.log('Returning OPQRST questions from application cache');
      this.questionsCache.set(type, this._applicationCacheService.applicationCache.opqrstQuestions);
      return of(this._applicationCacheService.applicationCache.opqrstQuestions);
    }

    // Check for History questions (Medical / Surgical History, Social History, Family History)
    const historyTypes = ['Medical / Surgical History', 'Social History', 'Family History'];
    if (historyTypes.includes(type) && this._applicationCacheService.applicationCache.historyQuestions?.length > 0) {
      console.log(`Returning ${type} questions from application cache`);
      const filteredQuestions = this._applicationCacheService.applicationCache.historyQuestions.filter(q => q.area === type);
      this.questionsCache.set(type, filteredQuestions);
      return of(filteredQuestions);
    }

    // No questions found in cache
    console.warn(`No ${type} questions found in cache. Questions should be loaded during initialization.`);
    return of([]);
  }

  /**
   * Load templates with smart caching
   * Templates are organization-wide and cached for the entire session
   * @param forceRefresh - Force refresh from server, bypassing cache
   * @returns Observable of templates
   */
  loadTemplates(forceRefresh: boolean = false): Observable<Template[]> {
    // Return cached templates if available and not forcing refresh
    if (!forceRefresh && this.templatesCache !== null && this.templatesCache.length > 0) {
      return of(this.templatesCache);
    }

    // Check if templates are in application cache
    if (!forceRefresh && this._applicationCacheService.applicationCache.templates?.length > 0) {
      this.templatesCache = this._applicationCacheService.applicationCache.templates;
      return of(this._applicationCacheService.applicationCache.templates);
    }

    // Fetch from API and cache
    const url = `${this.apiBaseUrl}api/v1/templates`;
    
    return this._dataService.get(url).pipe(
      tap((templates: Template[]) => {
        // Store in local cache
        this.templatesCache = templates;
        
        // Store in application cache for persistence
        this._applicationCacheService.setTemplatesCache(templates);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Search templates with optional title filter
   * This method bypasses cache and always fetches from API for accurate search results
   * @param title - Optional title to search for
   * @returns Observable of filtered templates
   */
  searchTemplates(title?: string): Observable<Template[]> {
    let url = `${this.apiBaseUrl}api/v1/templates`;
    if (title && title.trim()) {
      url += `?title=${encodeURIComponent(title.trim())}`;
    }
    
    return this._dataService.get(url).pipe(
      tap((templates: Template[]) => {
        // If searching without filters, update cache
        if (!title || !title.trim()) {
          this.templatesCache = templates;
          this._applicationCacheService.setTemplatesCache(templates);
        }
      }),
      catchError((error) => {
        console.error('Error searching templates:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Clear templates cache
   * Use this when templates are created, updated, or deleted to force refresh
   */
  clearTemplatesCache(): void {
    this.templatesCache = null;
    this._applicationCacheService.clearTemplatesCache();
  }

  /**
   * Patch one cached template in place (title rename, etc.) without waiting
   * for the full templates list request.
   */
  /** Replace the in-memory templates list used by schedule/encounter pickers. */
  replaceTemplatesCache(templates: Template[]): void {
    this.templatesCache = templates ?? [];
    this._applicationCacheService.setTemplatesCache(this.templatesCache);
  }

  upsertCachedTemplate(partial: Partial<Template> & { id?: string }): void {
    const id = String(partial?.id ?? "").trim();
    if (!id) {
      return;
    }
    const current = [
      ...(this.templatesCache ??
        this._applicationCacheService.applicationCache.templates ??
        []),
    ];
    const index = current.findIndex((t) => String(t?.id ?? "").trim() === id);
    if (index >= 0) {
      current[index] = { ...current[index], ...partial, id };
    } else {
      current.push({ ...partial, id } as Template);
    }
    this.templatesCache = current;
    this._applicationCacheService.setTemplatesCache(current);
  }

  /**
   * Load prompts with smart caching
   * Prompts are organization-wide and cached for the entire session
   * @param forceRefresh - Force refresh from server, bypassing cache
   * @returns Observable of prompts
   */
  loadPrompts(forceRefresh: boolean = false): Observable<Prompt[]> {
    // Return cached prompts if available and not forcing refresh
    if (!forceRefresh && this.promptsCache !== null && this.promptsCache.length > 0) {
      return of(this.promptsCache);
    }

    // Check if prompts are in application cache
    if (!forceRefresh && this._applicationCacheService.applicationCache.prompts?.length > 0) {
      this.promptsCache = this._applicationCacheService.applicationCache.prompts;
      return of(this._applicationCacheService.applicationCache.prompts);
    }

    // Fetch from API and cache
    const url = `${this.apiBaseUrl}api/v1/prompts`;
    
    return this._dataService.get(url).pipe(
      tap((prompts: Prompt[]) => {
        // Store in local cache
        this.promptsCache = prompts;
        
        // Store in application cache for persistence
        this._applicationCacheService.setPromptsCache(prompts);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Clear prompts cache
   * Use this when prompts are updated to force refresh
   */
  clearPromptsCache(): void {
    this.promptsCache = null;
    this._applicationCacheService.clearPromptsCache();
  }

  /**
   * Get all company users (id, firstName, lastName).
   */
  allUsers(): Observable<CompanyUser[]> {
    if (this.allUsersCache$) {
      return this.allUsersCache$;
    }
    const url = `${this.accountsApiBaseUrl}api/v1/users/company`;
    this.allUsersCache$ = this._dataService.get(url).pipe(
      catchError(() => of([])),
      map((response: any) => {
        const users: any[] = Array.isArray(response) ? response : [];
        const extracted = !Array.isArray(response) && response && typeof response === 'object'
          ? response.data ?? response.users ?? response.items ?? []
          : users;
        if (!Array.isArray(extracted) || extracted.length === 0) return [];
        return extracted
          .map((u: any) => {
            const id = u.id ?? u.userId ?? u.user_id;
            const firstName = u.firstName ?? u.first_name ?? '';
            const lastName = u.lastName ?? u.last_name ?? '';
            const email = String(
              u.email ?? u.Email ?? u.emailAddress ?? u.email_address ?? ''
            ).trim();
            if (id && (firstName || lastName)) {
              return {
                id: String(id),
                firstName,
                lastName,
                ...(email ? { email } : {}),
              };
            }
            return null;
          })
          .filter((u): u is CompanyUser => u != null);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.allUsersCache$;
  }

  clearAllUsersCache(): void {
    this.allUsersCache$ = null;
  }

  clearProvidersAndResourcesCache(): void {
    this.providersCache$ = null;
    this.resourcesCache$ = null;
  }

  getProviders(): Observable<Provider[]> {
    if (!this.providersCache$) {
      const url = `${this.accountsApiBaseUrl}api/v1/users/providers`;
      this.providersCache$ = this._dataService.get(url).pipe(
        catchError((error) => {
          console.error("✗ Error loading providers:", error);
          return of([] as Provider[]);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.providersCache$;
  }

  getResources(): Observable<Resource[]> {
    if (!this.resourcesCache$) {
      const url = `${this.accountsApiBaseUrl}api/v1/users/schedule-resources`;
      this.resourcesCache$ = this._dataService.get(url).pipe(
        catchError((error) => {
          console.error("✗ Error loading schedule resources:", error);
          return of([] as Resource[]);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.resourcesCache$;
  }

  getProcedureRoomsInfo(): Observable<ProcedureRoomInfo[]> {
    const url = `${this.accountsApiBaseUrl}api/v1/locations/company`;
    return this._dataService.get(url);
  }

  getChecklistQuestions(): Observable<ChecklistQuestion[]> {
    const url = `${this.apiBaseUrl}api/v1/question`;
    return this._dataService.get(url);
  }

  getEncounterChecklistQuestions(
    encounterId: string,
    companyChecklistQuestions: ChecklistQuestion[]
  ): Observable<EncounterChecklistQuestion[]> {
    if (this.isEncounterQuestionsAlreadyLoaded) {
      return of(this.checklistQuestionsCache);
    }
    const url = `${this.apiBaseUrl}api/v1/question/encounter/${encounterId}`;
    return this._dataService.get(url).pipe(
      catchError((error) => {
          this._applicationCacheService.setApiStatus(
            ApiStatus.Failed,
            ReferralApis.ChecklistQuestions
          );
        return throwError(() => error);
      }),
      tap((response: EncounterChecklistQuestion[]) => {
        const updatedQuestions: ChecklistQuestionsCache[] =
          companyChecklistQuestions.map((question) => {
            const matchingResponse = response.find(
              (r) => r.questionId === question.id
            );
            return {
              questionId: question.id,
              area: question.area,
              index: question.index,
              label: question.label,
              title: question.title,
              id: matchingResponse?.id,
              answer: matchingResponse?.answer,
              value: matchingResponse?.value,
            };
          });

        this._applicationCacheService.setChecklistQuestionsCache(
          updatedQuestions
        );
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.ChecklistQuestions
        );
      })
    );
  }

  saveChecklistQuestions(
    patientId: string,
    encounterId: string,
    procedureId: string,
    questions: EncounterChecklistQuestion[]
  ): Observable<EncounterChecklistQuestion[]> {
    const url = `${this.apiBaseUrl}api/v1/question`;
    return this._dataService.post(url, {
      patientId,
      procedureId,
      encounterId,
      questions,
    });
  }

  getGlobalSnacks(): Observable<EnumerationItem[]> {
    const url = `${this.apiBaseUrl}api/v1/lookup/global/snacks`;
    return this._dataService.get(url).pipe(
      map((items) => this.mapGlobalLookupToEnumerationItems(items)),
      catchError(() => of([] as EnumerationItem[]))
    );
  }

  getGlobalSupplies(): Observable<EnumerationItem[]> {
    const url = `${this.apiBaseUrl}api/v1/lookup/global/supplies`;
    return this._dataService.get(url).pipe(
      map((items) => this.mapGlobalLookupToEnumerationItems(items)),
      catchError(() => of([] as EnumerationItem[]))
    );
  }

  /** Maps GET `api/v1/lookup/global/{snacks|supplies}` rows to ng-select options. */
  mapGlobalLookupToEnumerationItems(items: unknown): EnumerationItem[] {
    if (!Array.isArray(items)) {
      return [];
    }
    return items
      .map((raw) => {
        const item = raw as Record<string, unknown>;
        const value = String(
          item["id"] ?? item["value"] ?? item["supplyId"] ?? item["snackId"] ?? ""
        ).trim();
        const displayName = String(
          item["name"] ??
            item["displayName"] ??
            item["title"] ??
            item["label"] ??
            value
        ).trim();
        if (!value || !displayName) {
          return null;
        }
        return { displayName, value };
      })
      .filter((x): x is EnumerationItem => x != null);
  }

  get accountsApiBaseUrl(): string {
    return environment.adminApiBaseUrl;
  }

  get apiBillerUrl(): string {
    return environment.billerApiBaseUrl;
  }

  private get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  get isEncounterQuestionsAlreadyLoaded(): boolean {
    return (
      this._applicationCacheService.getApiStatus(
        ReferralApis.ChecklistQuestions
      ) === ApiStatus.Success
    );
  }

  get checklistQuestionsCache(): ChecklistQuestionsCache[] {
    return (
      cloneDeep(
        this._applicationCacheService.applicationCache.checklistQuestions
      ) || []
    );
  }
}
